import { createApp } from '@backstage/app-defaults';
import { Entity } from '@backstage/catalog-model';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  AlertDisplay,
  AutoLogout,
  OAuthRequestDialog,
  Page,
  SignInPage,
  SignInProviderConfig,
  SupportButton,
} from '@backstage/core-components';
import { TechdocsEditorPage } from '@estehsaan/backstage-plugin-techdocs-editor';
import {
  IdentityApi,
  discoveryApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { ExportCsv } from '@material-table/exporters';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { CatalogImportPage } from '@backstage/plugin-catalog-import';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { Nobl9ProjectFieldExtension } from './scaffolder/nobl9/Nobl9ProjectFieldExtension';
import { Nobl9ServiceFieldExtension } from './scaffolder/nobl9/Nobl9ServiceFieldExtension';
import { Nobl9DataSourceFieldExtension } from './scaffolder/nobl9/Nobl9DataSourceFieldExtension';
import { Nobl9NfrProjectFieldExtension } from './scaffolder/nobl9/Nobl9NfrProjectFieldExtension';
import { Nobl9JourneyFieldExtension } from './scaffolder/nobl9/Nobl9JourneyFieldExtension';
import { Nobl9PfgOwnerFieldExtension } from './scaffolder/nobl9/Nobl9PfgOwnerFieldExtension';
import { CustomSearchPage } from '@internal/backstage-plugin-custom-search-ui-extension';
import {
  PanelType,
  TechDocsCustomHome,
  TechDocsReaderPage,
  TechDocsReaderPageContent,
  TechDocsReaderPageHeader,
  TechDocsReaderPageSubheader,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { Tool, ToolboxPage } from '@drodil/backstage-plugin-toolbox';
import { WorkstationConfigurationTool } from '@internal/plugin-workstation-configuration';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { GraphiQLPage } from '@backstage-community/plugin-graphiql';
import CssBaseline from '@material-ui/core/CssBaseline';
import {
  UnifiedThemeProvider,
  createUnifiedThemeFromV4,
} from '@backstage/theme';
import { SwaggerEditorComponent } from '@principal-developer-portal/api-portal-plugins';
import { MarketplaceFrontendPage } from '@internal/plugin-marketplace-frontend';
import {
  principalDarkTheme,
  principalLightTheme,
} from '@principal-developer-portal/ui';
import '@principal/design-system/pds-styles.min.css';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

import { Navigate, Route } from 'react-router-dom';
import { FeedbackAddon } from './addons/techdocs-feedback/src/plugin';
import { TechDocsRedirectExtension } from './addons/techdocsRedirect';
import { environmentConfigProviderApi } from './apis/environmentConfigProvider';
import { graphqlApi } from './apis/graphql';
import { vendorApi } from './apis/vendor';
import { Root } from './components/Root';
import { CustomCatalogIndexPage } from './components/catalog/CustomCatalogPage/plugin';
import { entityPage } from './components/catalog/EntityPage';
import { FeatureFlags } from './components/feature-flags/FeatureFlags';
import { HomePage } from './components/home/HomePage';
import { customSearchPage } from './components/search/CustomSearchPage';
import { ExportColumn, processColumnsForExport } from './utils/exportUtils';

import { CustomBootErrorPage } from './errorpages/CustomBootErrorPage';
import { CustomNotFoundErrorPage } from './errorpages/CustomNotFoundErrorPage';
import { RegexValidator } from './regex-validator/RegexValidator';
import { CodeSnippetGenerator } from './code-snippet-generator/CodeSnippetGenerator';
import { SbomPowerBI } from './sbom-powerbi/SbomPowerBi';
import { CronHelper } from './cron-helper/CronHelper';
import {
  AWSWorkloadCreateRepositoryFieldExtension,
  AWSWorkloadExistingRepositoryFieldExtension,
  AWSWorkloadOIDCRoleFieldExtension,
  AWSWorkloadRepositoryNameFieldExtension,
} from './scaffolder/aws-workload/CICD';
import { AWSWorkloadGovernanceGroupMembershipFieldExtension } from './scaffolder/aws-workload/GovernanceGroupMembership';
import { AWSWorkloadRequesterFieldExtension } from './scaffolder/aws-workload/Requester';
import { AWSWorkloadTextFieldExtension } from './scaffolder/aws-workload/TextField';
import {
  AWSWorkloadPublicSubnetFieldExtension,
  AWSWorkloadVPCAttachmentFieldExtension,
} from './scaffolder/aws-workload/VpcAttachment';
import { AssociateAdminFieldExtension } from './scaffolder/team-creation/AssociateAdminGroup';
import {
  GovernanceGroupFieldExtension,
  OrgMembershipFieldExtension,
} from './scaffolder/team-creation/GovernanceGroupMembership';
import { TeamMembershipFieldExtension } from './scaffolder/team-creation/GovernanceGroupMembership/extensions';
import { GitHubTeamNameExtension } from './scaffolder/team-creation/github-teams';
import {
  GitHubRepoNameStatusFieldExtension,
  UserTeamsPickerFieldExtension,
  OrgTeamsPickerFieldExtension,
  GitignorePickerFieldExtension,
  LifecyclePickerFieldExtension,
  TagFieldExtension,
  CopyCodefilesFieldExtension,
} from './scaffolder/github-repo/extensions';
import { Rewrite } from './utils/rewrite';
import { TokenRefresher } from './utils/tokenRefresher';
import { GraphiQLTool } from './graphiql-tool/GraphiQLTool';
import { AWSWorkloadAppInventoryIdFieldExtension } from './scaffolder/aws-workload/AppInventoryId';
import { LeanIXAppIdFieldExtension } from './scaffolder/governance-repo/LeanIXAppId';
import { AnalyticsListener } from '@principal-developer-portal/plugin-analytics-frontend';
import { AWSWorkloadEnvironmentFieldExtension } from './scaffolder/aws-workload/Environment/extensions';
import { setTokenCookie } from './cookieAuth';
import { SearchAddon } from './addons/techdocs-search/plugin';
import { ActionsDashboard, InsightsDashboard } from '@internal/yardstick-react';
import { PdpFilterFrontendPage } from '@internal/backstage-plugin-pdp-filter-frontend';
import EmbeddedSpectralLinter from './spectral/spectral';
import InfoIcon from '@material-ui/icons/Info';
import BackstageTokenGenerator from './backstage-token-generator/BackstageTokenGenerator';
import { WordToMD } from './word-to-md/WordToMD';
// import { LandingPage } from './components/home/LandingPage';
import CharacterCounter from './character-counter/CharacterCounter';
import { pdpLoggerFrontendPlugin } from '@internal/plugin-pdp-logger-frontend';
import { appConfigPlugin } from '@internal/plugin-appconfig-frontend';
import { YardstickIssuePreviewer } from './yardstick-issue-previewer/YardstickIssuePreviewer';
import { YardstickMeasurementRunner } from './yardstick-measurement-runner/YardstickMeasurementRunner';
import { CatalogProviderTrigger } from './catalog-provider-trigger/CatalogProviderTrigger';
import { Nobl9MetadataProvider } from '@internal/plugin-nobl9-metadata-frontend/src/Nobl9MetadataContext';
import { EmailBuilder } from './email-builder/EmailBuilder';
import { UnicodeToPlainText } from './unicode-to-plain-text/UnicodeToPlainText';
import { isPDPTeamMemberPermission } from './permissions';
import { OAuthConsentPage } from './components/auth/OAuthConsentPage';

const unifiedDarkTheme = createUnifiedThemeFromV4(principalDarkTheme);
const unifiedLightTheme = createUnifiedThemeFromV4(principalLightTheme);

const microsoftAuthProvider: SignInProviderConfig = {
  id: 'azure-auth-provider',
  title: 'Workforce Single Sign-On',
  message: 'Sign in using your workforce account.',
  apiRef: microsoftAuthApiRef,
};

// Module-level variable to store user groups (updated on sign-in)
let currentUserGroups: string[] = [];

const app = createApp({
  // add the pdpLoggerFrontendPlugin to apis to register the apiref api factory
  apis: [
    ...vendorApi,
    ...environmentConfigProviderApi,
    ...graphqlApi,
    ...pdpLoggerFrontendPlugin.getApis(),
    ...appConfigPlugin.getApis(),
  ],
  components: {
    NotFoundErrorPage: () => {
      return <CustomNotFoundErrorPage />;
    },
    BootErrorPage: () => {
      return <CustomBootErrorPage />;
    },
    SignInPage: props => {
      const discoveryApi = useApi(discoveryApiRef);
      return (
        <SignInPage
          {...props}
          auto
          provider={microsoftAuthProvider}
          onSignInSuccess={async (identityApi: IdentityApi) => {
            setTokenCookie(
              await discoveryApi.getBaseUrl('cookie'),
              identityApi,
            );

            props.onSignInSuccess(identityApi);

            // Update module-level variable with user groups
            currentUserGroups =
              (await identityApi.getBackstageIdentity())?.ownershipEntityRefs ||
              [];
          }}
        />
      );
    },
  },
  featureFlags: [],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {});
    bind(scaffolderPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'principal-dark-theme',
      title: 'Principal Dark Theme',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={unifiedDarkTheme}>
          <CssBaseline>{children}</CssBaseline>
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'principal-light-theme',
      title: 'Principal Light Theme',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={unifiedLightTheme}>
          <CssBaseline>{children}</CssBaseline>
        </UnifiedThemeProvider>
      ),
    },
  ],
});

const techDocsTabsConfig = [
  {
    label: 'Recommended',
    panels: [
      {
        title: '',
        description: '',
        panelType: 'DocsCardGrid' as PanelType,
        filterPredicate: (entity: Entity) =>
          (entity?.metadata?.tags?.includes('recommended') &&
            entity?.metadata?.tags?.includes('engineering-handbook')) ??
          false,
      },
    ],
  },
  {
    label: 'Owned',
    panels: [
      {
        title: 'Owned Documentation',
        description: 'Documentation owned by groups you are a member of',
        panelType: 'DocsCardGrid' as PanelType,
        filterPredicate: (entity: Entity) =>
          entity?.relations?.some(
            relation =>
              relation.type === 'ownedBy' &&
              currentUserGroups.includes(relation.targetRef),
          ) ?? false,
      },
    ],
  },
  {
    label: 'All',
    panels: [
      {
        title: 'All',
        description: 'All documentation that is registered in PDP',
        panelType: 'DocsTable' as PanelType,
        filterPredicate: () => true,
      },
    ],
  },
];

const workstationConfigurationTool: Tool = {
  id: 'workstation-configuration',
  name: 'Workstation Configuration',
  component: <WorkstationConfigurationTool />,
  description:
    'This tool is used to generate starter configuration files for common developer tools.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const regexValidator: Tool = {
  id: 'regex-validator',
  name: 'Regex Validator',
  component: <RegexValidator />,
  category: 'Validate',
  description:
    'JavaScript based Regex Validator tool, used to check if the regular expression matches the text.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const codeSnippetGenerator: Tool = {
  id: 'code-snippet-generator',
  name: 'Code Snippet Generator',
  component: <CodeSnippetGenerator />,
  category: 'Generate',
  description:
    'Quick and easy code snippet generator for various programming languages.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const sbomPowerBITool: Tool = {
  id: 'sbom-powerbi',
  name: 'SBOM PowerBI',
  component: <SbomPowerBI />,
  description:
    'Analyze GitHub software bill of materials data via Snowflake and PowerBI.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const backstageTokenGenerator: Tool = {
  id: 'backstage-token-generator',
  name: 'Backstage Token Generator',
  component: <BackstageTokenGenerator />,
  category: 'Backstage',
  description:
    'Generate a backstage token that can be used to authenticate with the backstage backend.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const wordToMDConverter: Tool = {
  id: 'word-to-md-converter',
  name: 'Word to Markdown Converter',
  component: <WordToMD />,
  category: 'Convert',
  description:
    'Converts a .docx file or a zipped folder of .docx files to a .md or zipped folder of .md files.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};
// Adding unicode to plain text logic

const unicodeToPlainText: Tool = {
  id: 'unicode-to-plain-text',
  name: 'Unicode to Plain Text',
  component: <UnicodeToPlainText />,
  category: 'Convert',
  description:
    'Decodes Unicode escape sequences (\\uXXXX, \\u{XXXXXX}, \\UXXXXXXXX, HTML entities) and normalizes fancy Unicode characters to plain ASCII text.',
  headerButtons: [
    <a
      href="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/support"
      rel="noopener noreferrer"
      aria-label="Support Documentation - Opens in new tab"
      style={{
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: principalLightTheme.palette.primary.main,
      }}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        window.open(
          '/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/support',
          '_blank',
          'noopener,noreferrer',
        );
      }}
    >
      <span>Support</span>
    </a>,
  ],
  showFavoriteButton: true,
  showOpenInNewWindowButton: false,
};

const characterCounter: Tool = {
  id: 'character-counter',
  name: 'Character Counter',
  component: <CharacterCounter />,
  category: 'Validate',
  description:
    'A simple tool to count characters, words, and sentences in a text input.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const cronHelper: Tool = {
  id: 'cron-helper',
  name: 'Cron Expression Helper',
  component: <CronHelper />,
  category: 'Generate',
  description:
    'Generate, validate, and preview cron expressions. Build cron schedules with an easy-to-use interface.',
  headerButtons: [
    <a
      href="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/support"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Support Documentation - Opens in new tab"
      style={{
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: principalLightTheme.palette.primary.main,
      }}
    >
      <InfoIcon
        aria-hidden="true"
        style={{
          marginLeft: '8px',
          verticalAlign: 'middle',
          color: principalLightTheme.palette.primary.main,
          width: '18px',
          height: '18px',
        }}
      />
      <span style={{ marginLeft: '4px' }}>Support</span>
    </a>,
  ],
  showFavoriteButton: true,
  showOpenInNewWindowButton: false,
};

const yardstickIssuePreviewer: Tool = {
  id: 'yardstick-issue-previewer',
  name: 'Yardstick issue previewer',
  component: <YardstickIssuePreviewer />,
  category: 'Yardstick',
  description:
    'Paste a Yardstick issue JSON payload to render the Insights Issue Action modal locally.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const yardstickMeasurementRunner: Tool = {
  id: 'yardstick-measurement-runner',
  name: 'Yardstick measurement runner',
  component: <YardstickMeasurementRunner />,
  category: 'Yardstick',
  description:
    'Choose an entity and measurement to trigger a Yardstick refresh and inspect the refreshed results without leaving PDP.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const emailBuilder: Tool = {
  id: 'email-builder',
  name: 'Email Builder',
  component: <EmailBuilder />,
  category: 'Generate',
  description:
    'Create custom emails with multiple sections using Handlebars templating. Choose background colors and generate HTML email templates.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const catalogProviderTrigger: Tool = {
  id: 'catalog-provider-trigger',
  name: 'Catalog Provider Trigger',
  component: <CatalogProviderTrigger />,
  category: 'Backstage',
  description:
    'Manually trigger a full refresh for any registered catalog entity provider. Restricted to IS EXE PDP members.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: false,
  showOpenInNewWindowButton: false,
};

const graphiqlTool: Tool = {
  id: 'graphql-playground',
  name: 'GraphQL Playground',
  component: <GraphiQLTool />,
  category: 'API',
  description:
    'Interactive GraphQL IDE for exploring GraphQL APIs. Test queries, mutations, and view schema documentation.',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const swaggerEditor: Tool = {
  id: 'swagger-editor',
  name: 'Swagger Editor (OpenAPI)',
  component: <SwaggerEditorComponent />,
  category: 'API',
  description: 'Design, document and test REST APIs visually',
  headerButtons: [<SupportButton />],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const spectralEditor: Tool = {
  id: 'spectral-editor',
  name: 'Spectral Editor (OpenAPI)',
  component: <EmbeddedSpectralLinter />,
  category: 'API',
  description: 'Ensure REST API quality with real-time linting and validation',

  headerButtons: [
    <a
      href="https://principal.service-now.com/esc?id=sc_cat_item_guide&table=sc_cat_item&sys_id=469bbe2a476d6d90291c87de536d43b2"
      target="_blank"
      rel="noopener noreferrer"
    >
      <InfoIcon
        style={{
          marginLeft: '8px',
          verticalAlign: 'middle',
          color: '#0066b3',
          width: '18px',
          height: '18px',
        }}
      />
      <span style={{ color: '#0066b3' }}>Raise an issue</span>
    </a>,
  ],
  showFavoriteButton: true,
  showOpenInNewWindowButton: true,
};

const techdocsPage = (
  <Page themeId="documentation">
    <TechDocsReaderPageHeader />
    <TechDocsReaderPageSubheader />
    <TechDocsReaderPageContent withSearch={false} />
  </Page>
);

// Custom export function that processes columns with render functions
const customExportFunc = (
  exportFn: (
    cols: ExportColumn[],
    datas: Array<Record<string, unknown>>,
    filename: string,
  ) => void,
  filename: string,
) => {
  return (cols: ExportColumn[], datas: Array<Record<string, unknown>>) => {
    const { processedCols, processedDatas } = processColumnsForExport(
      cols,
      datas,
    );
    exportFn(processedCols, processedDatas, filename);
  };
};

const routes = (
  <FlatRoutes>
    <Route path="/oauth2/authorize/:sessionId" element={<OAuthConsentPage />} />
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogIndexPage
        initiallySelectedFilter="owned"
        tableOptions={{
          exportAllData: true,
          exportMenu: [
            {
              label: 'Export CSV',
              exportFunc: customExportFunc(ExportCsv, 'catalog-entities'),
            },
          ],
        }}
      />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/docs"
      element={<TechDocsCustomHome tabsConfig={techDocsTabsConfig} />}
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      {techdocsPage}
      <TechDocsAddons>
        <Mermaid />
        <ReportIssue />
        <FeedbackAddon />
        <TechDocsRedirectExtension />
        <SearchAddon />
      </TechDocsAddons>
    </Route>

    <Route
      path="/docs/:namespace/:kind/:name/edit"
      element={<TechdocsEditorPage />}
    />
    <Route
      path="/tech-radar"
      element={
        <Rewrite to="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/techradar" />
      }
    />
    <Route path="/create" element={<Rewrite to="/generate" />} />
    <Route path="/marketplace" element={<MarketplaceFrontendPage />} />
    <Route
      path="/docs/default/component/principal-engineering-handbook"
      element={
        <Rewrite to="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook" />
      }
    />
    {/* Support the external redirects of handbook as the new `mkdocs-redirects` plugin doesn't support external redirects */}
    <Route
      path="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/architecture/patterns/ingestionhub"
      element={
        <Rewrite
          to="https://github.com/principalfinancialgroup-emu/principal-engineering-handbook/pull/772/#pullrequestreview-2139449003"
          external
        />
      }
    />
    <Route
      path="/generate/templates/default/create-catalog-info-yaml-template"
      element={
        <Rewrite to="/generate/templates/principalinformationservices-emu/principal-developer-portal-templates-create-catalog-info" />
      }
    />
    <Route
      path="/generate/templates/principalinformationservices-emu/create-catalog-info-yaml-template"
      element={
        <Rewrite to="/generate/templates/principalinformationservices-emu/principal-developer-portal-templates-create-catalog-info" />
      }
    />
    <Route
      path="/generate/templates/principalinformationservices-emu/azure-template-beta"
      element={
        <Rewrite to="/generate/templates/principalinformationservices-emu/principal-developer-portal-templates-team-creation" />
      }
    />
    <Route
      path="/generate/templates/principalinformationservices-emu/azure-template"
      element={
        <Rewrite to="/generate/templates/principalinformationservices-emu/principal-developer-portal-templates-team-creation" />
      }
    />
    <Route
      path="/generate/templates/principalinformationservices-emu/principal-developer-portal-exceptions-template"
      element={
        <Rewrite to="/generate/templates/principalinformationservices-emu/principal-developer-portal-templates-exceptions" />
      }
    />
    <Route
      path="/generate/templates/default/template-to-adopt-common-workflows-maven-jar"
      element={
        <Rewrite to="/generate/templates/principalfinancialgroup-emu/enterprise-pipeline-pdp-templates-adopt-pipeline-template" />
      }
    />
    <Route
      path="/generate/templates/default/template-to-adopt-this-pipeline-liberty-deployment"
      element={
        <Rewrite to="/generate/templates/principalfinancialgroup-emu/enterprise-pipeline-pdp-templates-adopt-pipeline-template" />
      }
    />
    <Route
      path="/generate/templates/default/template-to-adopt-this-pipeline-websphere-deployment"
      element={
        <Rewrite to="/generate/templates/principalfinancialgroup-emu/enterprise-pipeline-pdp-templates-adopt-pipeline-template" />
      }
    />

    <Route
      path="/generate/templates/default/adopt-pipeline-template"
      element={
        <Rewrite to="/generate/templates/principalfinancialgroup-emu/enterprise-pipeline-pdp-templates-adopt-pipeline-template" />
      }
    />

    <Route
      path="/generate/templates/principalfinancialgroup-emu/adopt-pipeline-template"
      element={
        <Rewrite to="/generate/templates/principalfinancialgroup-emu/enterprise-pipeline-pdp-templates-adopt-pipeline-template" />
      }
    />

    <Route
      path="/generate"
      element={
        <Nobl9MetadataProvider>
          <ScaffolderPage />
        </Nobl9MetadataProvider>
      }
    >
      <ScaffolderFieldExtensions>
        <Nobl9ProjectFieldExtension />
        <Nobl9ServiceFieldExtension />
        <Nobl9DataSourceFieldExtension />
        <Nobl9NfrProjectFieldExtension />
        <Nobl9JourneyFieldExtension />
        <Nobl9PfgOwnerFieldExtension />
        <AWSWorkloadGovernanceGroupMembershipFieldExtension />
        <AWSWorkloadExistingRepositoryFieldExtension />
        <AWSWorkloadOIDCRoleFieldExtension />
        <AWSWorkloadCreateRepositoryFieldExtension />
        <AWSWorkloadRepositoryNameFieldExtension />
        <AWSWorkloadVPCAttachmentFieldExtension />
        <AWSWorkloadPublicSubnetFieldExtension />
        <AWSWorkloadRequesterFieldExtension />
        <AWSWorkloadTextFieldExtension />
        <AWSWorkloadAppInventoryIdFieldExtension />
        <LeanIXAppIdFieldExtension />
        <AWSWorkloadEnvironmentFieldExtension />
        <GitHubTeamNameExtension />
        <GitHubRepoNameStatusFieldExtension />
        <UserTeamsPickerFieldExtension />
        <OrgTeamsPickerFieldExtension />
        <GitignorePickerFieldExtension />
        <LifecyclePickerFieldExtension />
        <TagFieldExtension />
        <CopyCodefilesFieldExtension />
        <GovernanceGroupFieldExtension />
        <OrgMembershipFieldExtension />
        <TeamMembershipFieldExtension />
        <AssociateAdminFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/search" element={<CustomSearchPage />}>
      {customSearchPage}
    </Route>
    {/* <FeatureFlagged with="new-home-page">
      <Route path="/" element={<LandingPage />} />
    </FeatureFlagged> */}
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />

    <Route
      path="/aws"
      element={
        <Rewrite to="/catalog?filters[kind]=resource&filters[type]=account" />
      }
    />
    <Route
      path="/aws/workload/change"
      element={
        <Rewrite to="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/infrastructure/aws/request-a-workload-account/#request-changes-to-your-workload-accounts" />
      }
    />
    <Route
      path="/aws/workload/create"
      element={
        <Rewrite to="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/infrastructure/aws/request-a-workload-account/#request-a-new-workload" />
      }
    />

    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/graphiql" element={<GraphiQLPage />} />
    <Route
      path="/health-and-metrics"
      element={<Rewrite to="/actions" excludeSearchParams />}
    />
    <Route path="/actions" element={<ActionsDashboard />} />
    <Route path="/insights" element={<InsightsDashboard />} />
    <Route
      path="/insights-actions"
      element={<Rewrite to="/actions" excludeSearchParams />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name/insights"
      element={
        <Rewrite
          to={params =>
            `/catalog/${params.namespace}/${params.kind}/${params.name}/insights-actions`
          }
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name/dora"
      element={
        <Rewrite to="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/dora-metrics" />
      }
    />
    <Route path="/marketplace" element={<MarketplaceFrontendPage />} />
    <Route
      path="/manage"
      element={<Rewrite to="/actions" excludeSearchParams />}
    />

    <Route
      path="/toolbox"
      element={
        <RequirePermission
          permission={isPDPTeamMemberPermission}
          errorPage={
            <ToolboxPage
              extraTools={[
                workstationConfigurationTool,
                regexValidator,
                codeSnippetGenerator,
                cronHelper,
                swaggerEditor,
                spectralEditor,
                graphiqlTool,
                backstageTokenGenerator,
                wordToMDConverter,
                unicodeToPlainText,
                sbomPowerBITool,
                characterCounter,
                yardstickIssuePreviewer,
                yardstickMeasurementRunner,
                // No emailBuilder for non-members
              ]}
            />
          }
        >
          <ToolboxPage
            extraTools={[
              workstationConfigurationTool,
              regexValidator,
              codeSnippetGenerator,
              cronHelper,
              swaggerEditor,
              spectralEditor,
              graphiqlTool,
              backstageTokenGenerator,
              wordToMDConverter,
              unicodeToPlainText,
              sbomPowerBITool,
              characterCounter,
              yardstickIssuePreviewer,
              yardstickMeasurementRunner,
              emailBuilder, // Only for PDP team members
              catalogProviderTrigger, // Only for PDP team members
            ]}
          />
        </RequirePermission>
      }
    />
    <Route
      path="/toolbox#workstation-configuration"
      element={<WorkstationConfigurationTool />}
    />
    <Route
      path="/config-tools"
      element={<Navigate to="/toolbox#workstation-configuration" />}
    />
  </FlatRoutes>
);

// Set initial expiry timestamp of 30 secs
const expiryTimestamp = new Date();
expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + 30);

export default app.createRoot(
  <>
    <FeatureFlags />
    <AlertDisplay />
    <OAuthRequestDialog />
    <AnalyticsListener />
    <AutoLogout />
    <TokenRefresher expiryTimestamp={expiryTimestamp} />
    <PdpFilterFrontendPage />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
